/**
 * Bundles checked complementary products with the main product form on add to cart.
 */
class ComplementaryProducts extends HTMLElement {
  constructor() {
    super();
    this._onSubmit = this._onSubmit.bind(this);
  }

  connectedCallback() {
    this._form = document.getElementById(this.getAttribute('form'));
    if (!this._form) return;

    this._form.addEventListener('submit', this._onSubmit, { capture: true });
  }

  disconnectedCallback() {
    this._form?.removeEventListener('submit', this._onSubmit, { capture: true });
  }

  _getCheckedAddons() {
    return Array.from(this.querySelectorAll('[data-complementary-addon]')).filter((addon) => {
      const checkbox = addon.querySelector('[data-complementary-checkbox]');
      return checkbox?.checked && !addon.hasAttribute('data-disabled');
    });
  }

  _buildMainItem(formData) {
    const item = {
      id: Number(formData.get('id')),
      quantity: Number(formData.get('quantity') || 1)
    };

    const sellingPlan = formData.get('selling_plan');
    if (sellingPlan) {
      item.selling_plan = Number(sellingPlan);
    }

    const properties = {};
    for (const [key, value] of formData.entries()) {
      const match = key.match(/^properties\[(.+)\]$/);
      if (match) {
        properties[match[1]] = value;
      }
    }

    if (Object.keys(properties).length > 0) {
      item.properties = properties;
    }

    return item;
  }

  async _onSubmit(event) {
    const checkedAddons = this._getCheckedAddons();
    if (checkedAddons.length === 0) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    const form = this._form;
    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    const submitButtons = Array.from(form.elements).filter((button) => button.type === 'submit');
    submitButtons.forEach((button) => button.setAttribute('aria-busy', 'true'));

    const formData = new FormData(form);
    const items = [this._buildMainItem(formData)];

    checkedAddons.forEach((addon) => {
      const variantId = addon.getAttribute('data-variant-id');
      const quantityInput = addon.querySelector('[data-complementary-qty-input]');
      const quantity = Number(quantityInput?.value || 1);

      if (variantId) {
        items.push({ id: Number(variantId), quantity });
      }
    });

    let sectionsToBundle = ['variant-added'];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', {
        bubbles: true,
        detail: { sections: sectionsToBundle }
      })
    );

    try {
      const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          items,
          sections: sectionsToBundle.join(','),
          sections_url: `${Shopify.routes.root}variants/${formData.get('id')}`
        })
      });

      const responseJson = await response.json();

      if (response.ok) {
        if (window.themeVariables.settings.cartType === 'page' || window.themeVariables.settings.pageType === 'cart') {
          window.location.href = `${Shopify.routes.root}cart`;
          return;
        }

        const cartContent = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
        cartContent.sections = responseJson.sections;

        form.dispatchEvent(
          new CustomEvent('variant:add', {
            bubbles: true,
            detail: {
              items: responseJson.items || [responseJson],
              cart: cartContent
            }
          })
        );

        document.documentElement.dispatchEvent(
          new CustomEvent('cart:change', {
            bubbles: true,
            detail: {
              baseEvent: 'variant:add',
              cart: cartContent
            }
          })
        );
      } else {
        form.dispatchEvent(
          new CustomEvent('cart:error', {
            bubbles: true,
            detail: { error: responseJson.description }
          })
        );
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      }
    } catch (error) {
      console.error('Failed to add complementary products', error);
      document.dispatchEvent(new CustomEvent('cart:refresh'));
    } finally {
      submitButtons.forEach((button) => button.removeAttribute('aria-busy'));
    }
  }
}

if (!window.customElements.get('complementary-products')) {
  window.customElements.define('complementary-products', ComplementaryProducts);
}
